/** @format */

import {useLocation, useNavigate} from 'react-router-dom';
import {ViewListOutlined, ViewModuleOutlined, School, SupportAgent} from '@mui/icons-material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import {useMediaQuery} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import Alert from '@mui/material/Alert';

import {HeaderMenuButton} from 'components/buttons/HeaderMenuButton';
import {useMixpanelContext} from 'contexts/MixpanelContext';
import {useAuthContext} from 'contexts/AuthContext';
import {useCompanyContext} from 'contexts/CompanyContext';
import {getMinutesLeft} from 'features/tariffs/helpers/getMinutesLeft';
import {getDaysLeft} from 'features/tariffs/helpers/getDaysLeft';
import {parseEndDate} from 'features/tariffs/helpers/parseEndDate';
import {isTrialSubscription} from 'features/tariffs/helpers/isTrialSubscription';

import {ROUTES} from 'constants/ROUTES';
import {getAnchor} from 'components/fabs/helpers/getAnchor';
import {TELEGRAM_SUPPORT_URL} from 'components/fabs/components/HelpFab/constants';

import {Root} from './styles';
import {
  useGetSubscription,
  useGetTariffNameQuery,
  useSignOutMutation,
} from '../graphql/__generated__';
import {AccountMenu} from '../AccountMenu';
import {HEADER_ROUTES, MAX_INVALID_MINUTES} from '../constants';

import {TariffsMenuButton} from '../TariffsMenuButton';
import {getActivePath} from './helpers/getActivePath';
import {HelpFab} from '../../fabs/components/HelpFab';

interface Props {
  onToggleDrawer?: () => void;
}

const NativeMenuNew = ({onToggleDrawer}: Props) => {
  const {mixpanel} = useMixpanelContext();
  const {pathname} = useLocation();
  const {currentCompany} = useCompanyContext();

  const authContext = useAuthContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const {
    data: dataSubscription,
    error: errorSubscription,
    loading: loadingSubscription,
  } = useGetSubscription();

  const isTrial = isTrialSubscription(dataSubscription?.currentUser?.subscription);

  const {
    data: dataName,
    error: errorName,
    loading: loadingName,
  } = useGetTariffNameQuery({
    skip: !dataSubscription || isTrial,
  });

  const [signOut, {error: signOutError}] = useSignOutMutation();

  if (signOutError) {
    return <Alert severity='error'>{signOutError.message}</Alert>;
  }

  const loading = loadingSubscription || loadingName;
  const error = errorSubscription || errorName;

  const {isValid: isValidDate, parsedEndDate} = parseEndDate(
    dataSubscription?.currentUser?.subscription.endDate,
  );
  const isParseError = error || (!isValidDate && !loading);

  if (isParseError) {
    return <Alert severity='error'>Что-то пошло не так</Alert>;
  }

  const minutesLeft = isValidDate ? getMinutesLeft(parsedEndDate) : Infinity;

  const daysLeft = isValidDate ? getDaysLeft(parsedEndDate) : Infinity;

  const tariffName = dataName?.currentUser?.subscription.tariffDetails?.name || '';

  const showTariffButton = !loading && (isTrial || minutesLeft <= MAX_INVALID_MINUTES);

  const isTariffExpired = !loading && daysLeft < 1;

  const handleClickProjects = () => {
    mixpanel.track('click_projects_top_menu');
    onToggleDrawer?.();
  };

  const handleClickChiefReport = () => {
    mixpanel.track('click_chief_report_top_menu');
    onToggleDrawer?.();
  };

  const handleClickTariffs = () => {
    mixpanel.track('click_tariffs_top_menu');
    onToggleDrawer?.();
  };

  const handleClickSettings = () => {
    mixpanel.track('click_settings_top_menu');
    onToggleDrawer?.();
  };

  const handleClickAccount = () => {
    mixpanel.track('click_account_top_menu');
    onToggleDrawer?.();
  };

  const handleLogout = async () => {
    mixpanel.track('click_sign_out_button');
    onToggleDrawer?.();

    try {
      await signOut();
      await authContext.signOut();
      // NOTE: temporary replace to explicit page reload for avoiding switch theme bug
      // history.push('/');
      location.replace('/');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const handleExpiredTariff = () => {
    navigate(0);
  };

  const handleClickTelegram = () => {
    mixpanel.track('click_help_telegram', {
      project_uuid: currentCompany?.uuid,
    });
    onToggleDrawer?.();
    window.open(TELEGRAM_SUPPORT_URL, '_blank', 'noopener,noreferrer');
  };

  const handleClickMaterials = () => {
    mixpanel.track('click_help_materials', {
      project_uuid: currentCompany?.uuid,
    });
    onToggleDrawer?.();
    const anchor = getAnchor(pathname);
    navigate(`/${ROUTES.HELP_MATERIALS}${anchor}`);
  };

  const isHideMaterials = pathname.startsWith('/help/materials');

  const activePath = getActivePath(pathname);

  return (
    <Root>
      {!isTariffExpired && (
        <>
          <HeaderMenuButton
            onClick={handleClickProjects}
            to={HEADER_ROUTES.PROJECTS}
            selected={activePath === HEADER_ROUTES.PROJECTS}
          >
            <ViewListOutlined />
            Проекты
          </HeaderMenuButton>
          <HeaderMenuButton
            onClick={handleClickChiefReport}
            to={HEADER_ROUTES.CHIEF_REPORT}
            selected={activePath === HEADER_ROUTES.CHIEF_REPORT}
          >
            <ViewModuleOutlined />
            Отчет руководителя
          </HeaderMenuButton>
        </>
      )}

      {showTariffButton && (
        <TariffsMenuButton
          minutesLeft={minutesLeft}
          isTrial={isTrial}
          tariffName={tariffName}
          onClick={handleClickTariffs}
          onExpiredByTimer={handleExpiredTariff}
        />
      )}

      <HeaderMenuButton
        onClick={handleClickSettings}
        to={HEADER_ROUTES.SETTINGS}
        selected={activePath === HEADER_ROUTES.SETTINGS}
      >
        <SettingsOutlinedIcon />
        Настройки
      </HeaderMenuButton>

      {isMobile ? (
        <>
          <HeaderMenuButton
            onClick={handleClickAccount}
            to={HEADER_ROUTES.ACCOUNT}
            selected={activePath === HEADER_ROUTES.ACCOUNT}
          >
            <AccountCircleOutlinedIcon />
            Настройки аккаунта
          </HeaderMenuButton>

          <HeaderMenuButton onClick={handleClickTelegram}>
            <SupportAgent />
            Поддержка в Телеграме
          </HeaderMenuButton>

          {!isHideMaterials && (
            <HeaderMenuButton onClick={handleClickMaterials}>
              <School />
              Обучающие материалы
            </HeaderMenuButton>
          )}

          <HeaderMenuButton onClick={handleLogout}>
            <ExitToAppIcon />
            Выход
          </HeaderMenuButton>
        </>
      ) : (
        <AccountMenu onClickExit={handleLogout} onClickAccount={handleClickAccount} />
      )}

      {!isMobile && <HelpFab />}
    </Root>
  );
};

export {NativeMenuNew};
